import { QuarterTurn, TurnMargins } from "@pdfking/filling-flex"
import type { Distance, SpatialRectangle } from "@pdfking/math"
import { Center, RemoveMargins, Subtract, ToSpatial } from "@pdfking/math"
import { HALF_TURN, ZERO_DEGREE } from "@pdfking/math/constants"
import { TurnBox } from "@pdfking/renderer-api"
import type { PageShape } from "@pdfking/renderer-api"
import { NullQ } from "@pdfking/structs"
import { Identity } from "@pdfking/tfunc"

import type { DrawFrame, Drawing, ImpositionNode, PlotCtx } from "#internal"

import { CreateGroup } from "../../../shapes/CreateGroup"
import { MirrorShapes } from "../../../utils/MirrorShapes"

// A box mirrored across the vertical line at `x`.
const mirror =
  (x: number) =>
  (box: SpatialRectangle<Distance>): SpatialRectangle<Distance> => ({
    ...box,
    x: (2 * x - box.x - box.width) as Distance,
  })

/**
 * The shapes of one drawing on `node`, on the sheet.
 *
 * A drawing hung on the sheet's frame, or on a node that has not turned since,
 * draws straight onto the sheet. One hung on a node turned since draws in the
 * frame it was hung in — the node's box stood back upright, at the origin —
 * and comes back as one group turning that onto where the node stands now.
 */
export const PlaceDrawing = (
  node: ImpositionNode,
  { draw, rotation }: Drawing,
  ctx: PlotCtx
): PageShape[] => {
  // A drawing in the node's frame on a node turned over is seen from behind:
  // mirrored across the node's middle. One on the sheet's frame reads where
  // things landed, and they landed turned over already.
  const behind = !NullQ(rotation) && !node.is_front
  const delta = NullQ(rotation) ? ZERO_DEGREE : QuarterTurn(Subtract(node.rotation, rotation))
  if (!delta) {
    const frame: DrawFrame = {
      outer: node.position,
      inner: node.page ? node.position : RemoveMargins(node.position, node.padding),
      // Mirrored after it is drawn, so what it reads off the sheet is
      // mirrored going in.
      from_sheet: behind ? mirror(Center(node.position).x) : Identity,
    }
    const shapes = [...draw(node, frame, ctx)]
    return behind ? MirrorShapes(shapes, Center(node.position).x) : shapes
  }

  const turned = Boolean(delta % HALF_TURN)
  const { width, height } = node.position
  const outer = ToSpatial(turned ? { width: height, height: width } : { width, height })
  const own = Center(outer)
  const landed = Center(node.position)
  const back = Subtract(ZERO_DEGREE, delta)
  const shapes = [
    ...draw(
      node,
      {
        outer,
        inner: node.page ? outer : RemoveMargins(outer, TurnMargins(node.padding, back)),
        from_sheet: box => {
          const turned = TurnBox(box, back, landed, {
            x: (own.x - landed.x) as Distance,
            y: (own.y - landed.y) as Distance,
          })
          return behind ? mirror(own.x)(turned) : turned
        },
      },
      ctx
    ),
  ]
  return CreateGroup(behind ? MirrorShapes(shapes, own.x) : shapes, {
    origin: own,
    rotation: delta,
    translate: { x: (landed.x - own.x) as Distance, y: (landed.y - own.y) as Distance },
  })
}
